export class StaleLiveKitConnectionAttemptError extends Error {
  constructor(
    readonly attemptId: number,
    readonly activeAttemptId: number
  ) {
    super(`LiveKit connection attempt ${attemptId} was superseded by ${activeAttemptId}`);
    this.name = "StaleLiveKitConnectionAttemptError";
  }
}

export function assertCurrentLiveKitConnectionAttempt(
  attemptId: number,
  activeAttemptId: number
): void {
  if (attemptId !== activeAttemptId) {
    throw new StaleLiveKitConnectionAttemptError(attemptId, activeAttemptId);
  }
}

export function resolveSpatiusViewerBootstrapFailure(
  sessionVersion: number,
  currentSessionVersion: number
): "ignore_stale" | "continue_base_room_audio" {
  return sessionVersion === currentSessionVersion ? "continue_base_room_audio" : "ignore_stale";
}

export type SessionTransportShutdownResult = {
  sessionControlSent: boolean;
  serverSessionShutdownRequested: boolean;
};

export type SessionTransportLocalIsolationResult =
  | { status: "completed" }
  | { status: "timed_out" }
  | { status: "failed"; error: unknown };

export type SessionTransportShutdownHandle = {
  localIsolation: Promise<SessionTransportLocalIsolationResult>;
  cleanup: Promise<SessionTransportShutdownResult>;
};

type SessionTransportShutdownInput<TRoom extends object> = {
  attemptId: number;
  room: TRoom;
  keepalive?: boolean;
  localIsolationTimeoutMs?: number;
  sessionControlTimeoutMs?: number;
  sendSessionControl: () => Promise<boolean>;
  requestServerShutdown: () => Promise<boolean>;
  resetViewerSession: () => void;
  disconnectRoom: () => Promise<void>;
};

export type SessionTransportShutdownCoordinator<TRoom extends object> = {
  run: (input: SessionTransportShutdownInput<TRoom>) => SessionTransportShutdownHandle;
};

async function isolateSessionTransport(
  disconnectRoom: () => Promise<void>,
  timeoutMs?: number
): Promise<SessionTransportLocalIsolationResult> {
  const disconnectResult = Promise.resolve()
    .then(disconnectRoom)
    .then<SessionTransportLocalIsolationResult>(() => ({ status: "completed" }))
    .catch<SessionTransportLocalIsolationResult>((error: unknown) => ({
      status: "failed",
      error,
    }));

  if (!timeoutMs || timeoutMs <= 0) {
    return disconnectResult;
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutResult = new Promise<SessionTransportLocalIsolationResult>((resolve) => {
    timeoutId = setTimeout(() => resolve({ status: "timed_out" }), timeoutMs);
  });

  try {
    return await Promise.race([disconnectResult, timeoutResult]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

async function sendSessionControlWithin(
  sendSessionControl: () => Promise<boolean>,
  timeoutMs?: number
): Promise<boolean> {
  let sendResult: Promise<boolean>;
  try {
    sendResult = Promise.resolve(sendSessionControl()).catch(() => false);
  } catch {
    sendResult = Promise.resolve(false);
  }
  if (!timeoutMs || timeoutMs <= 0) {
    return sendResult;
  }

  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  const timeoutResult = new Promise<boolean>((resolve) => {
    timeoutId = setTimeout(() => resolve(false), timeoutMs);
  });

  try {
    return await Promise.race([sendResult, timeoutResult]);
  } finally {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  }
}

export function createSessionTransportShutdownCoordinator<
  TRoom extends object,
>(): SessionTransportShutdownCoordinator<TRoom> {
  type ShutdownOperation = {
    handle: SessionTransportShutdownHandle;
    keepaliveServerShutdownRequested: boolean;
    keepaliveServerShutdownPromise?: Promise<boolean>;
  };
  const operationsByRoom = new WeakMap<TRoom, ShutdownOperation>();

  return {
    run(input) {
      const existing = operationsByRoom.get(input.room);
      if (existing) {
        if (input.keepalive && !existing.keepaliveServerShutdownRequested) {
          existing.keepaliveServerShutdownRequested = true;
          existing.keepaliveServerShutdownPromise = input.requestServerShutdown();
        }
        return existing.handle;
      }

      const operation: ShutdownOperation = {
        keepaliveServerShutdownRequested: input.keepalive === true,
        handle: {
          localIsolation: Promise.resolve({ status: "completed" }),
          cleanup: Promise.resolve({
            sessionControlSent: false,
            serverSessionShutdownRequested: false,
          }),
        },
      };

      const sessionControlPromise = sendSessionControlWithin(
        input.sendSessionControl,
        input.sessionControlTimeoutMs
      );
      const initialServerShutdownPromise = input.requestServerShutdown();
      if (input.keepalive) {
        operation.keepaliveServerShutdownPromise = initialServerShutdownPromise;
      }

      const localIsolation = (async (): Promise<SessionTransportLocalIsolationResult> => {
        let viewerResetError: unknown;
        try {
          input.resetViewerSession();
        } catch (error) {
          viewerResetError = error;
        }

        const disconnectResult = await isolateSessionTransport(
          input.disconnectRoom,
          input.localIsolationTimeoutMs
        );
        if (viewerResetError !== undefined) {
          return { status: "failed", error: viewerResetError };
        }
        return disconnectResult;
      })();

      const cleanup = (async () => {
        const [sessionControlSent, initialServerShutdownRequested] = await Promise.all([
          sessionControlPromise,
          initialServerShutdownPromise,
        ]);
        const keepaliveServerShutdownRequested =
          operation.keepaliveServerShutdownPromise &&
          operation.keepaliveServerShutdownPromise !== initialServerShutdownPromise
            ? await operation.keepaliveServerShutdownPromise
            : false;
        await localIsolation;
        return {
          sessionControlSent,
          serverSessionShutdownRequested:
            initialServerShutdownRequested || keepaliveServerShutdownRequested,
        };
      })();

      operation.handle = {
        localIsolation,
        cleanup,
      };
      operationsByRoom.set(input.room, operation);
      return operation.handle;
    },
  };
}
